import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  setOpenModalType,
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSort,
} from "../../../../../features/metaEntitiesSlice";
import { useDispatch } from "react-redux";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import Link from "@mui/material/Link";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditCollectionTypesModal from "../CollectionTypesModal/EditCollectionTypesModal";
import { selectUserData } from "../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import store from "../../../../../store";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { formatDate } from '../../../../../utils/helpers';

export const CollectionTypesTable = () => {
  const {
    selectedEntityType,
    metaEntityList,
    sort,
    page,
    rowsPerPage,
    openModalType,
  } = useSelector((state: any) => state?.metaEntity);
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const response = useSelector(selectUserData);
  const hasEditCollectionTypesPermission = CheckPermissions(
    response?.data?.products?.admin,
    "meta_entities",
    "edit_collection_types"
  );

  const filteredCollectionTypesData = sortedData?.filter((metaName: any) => {
    const selectedFormatTypeName =
      selectedEntityType && selectedEntityType.length !== 0
        ? selectedEntityType.includes(metaName?.collection_type_name)
        : true;
    return selectedFormatTypeName;
  });

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const tableheaders = [
    {
      id: 0,
      KEY: "collection_type_name",
      label: "Collection Type Name",
      sortable: true,
    },
    { id: 1, KEY: "created", label: "Created On", sortable: true },
    { id: 2, KEY: "created_by", label: "Created By", sortable: true },
    { id: 3, KEY: "modified", label: "Last edited", sortable: true },
    { id: 3, KEY: "modified_by", label: "Edited By", sortable: true },
  ];
  const sortedRows = getSortedData(filteredCollectionTypesData || []);

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));

    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    store.dispatch(setPage(0));
  };

  //-- Function to open modal(create / edit)
  const handleModalOpen = (modalType: string, row: any) => {
    dispatch(setOpenModalType(modalType));
    dispatch(setSelectedRows(row)); // selected row
  };

  //-- Function to close modal(create / edit)
  const handleCloseModal = () => {
    dispatch(setOpenModalType(null));
  };

  //-- Set Sorted Data
  useEffect(() => {
    const sortedRows = getSortedData(metaEntityList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [metaEntityList, getSortedData]);

  useEffect(() => {
    dispatch(setPage(0));
    // eslint-disable-next-line
  }, [selectedEntityType]);

  useEffect(() => {
    dispatch(setSelectedRows([]));
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  return (
    <>
      <TableContainer className="PageScantablewrap">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCollectionTypesData?.length > 0 ? (
              filteredCollectionTypesData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: any) => (
                  <TableRow key={index}>
                    <TableCell align="left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {hasEditCollectionTypesPermission ? (
                          <Link href="#" color="secondary">
                            <EditOutlinedIcon
                              data-tracker="234"
                              onClick={() => handleModalOpen("edit", row)}
                              fontSize="small"
                            />
                          </Link>
                        ) : (
                          <Link
                            href="#"
                            color="secondary"
                            sx={{ pointerEvents: "none", color: "gray" }}
                            aria-disabled="true"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </Link>
                        )}

                        <Typography variant="body1" fontSize={14}>
                          <Stack
                            alignItems="center"
                            gap={1}
                            flexDirection="row"
                          >
                            {row?.collection_type_name}
                          </Stack>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell align="left">
                      {row?.created ? formatDate(row?.created) : "N/A"}
                    </TableCell>

                    <TableCell align="left">
                      {row?.created_by ? row?.created_by : "N/A"}
                    </TableCell>

                    <TableCell align="left">
                      {row?.modified ? formatDate(row?.modified) : "N/A"}
                    </TableCell>

                    <TableCell align="left">
                      {row?.modified_by ? row?.modified_by : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No Data Available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredCollectionTypesData?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>

      {openModalType === "edit" && (
        <EditCollectionTypesModal open={true} handleClose={handleCloseModal} />
      )}
    </>
  );
};
